(() => {
  const DISCOUNT_LABEL = 'Economize';

  const fallbackFormatMoney = (cents) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format((Number(cents) || 0) / 100);
  };

  const formatBundleMoney = (cents, moneyFormat) => {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, moneyFormat);
    }

    return fallbackFormatMoney(cents);
  };

  const parseVariantData = (variantSelects) => {
    if (!variantSelects) return [];

    const variantJson = variantSelects.querySelector('script[type="application/json"]');
    if (!variantJson) return [];

    try {
      return JSON.parse(variantJson.textContent);
    } catch (error) {
      console.warn('Bundle selector could not parse variant data.', error);
      return [];
    }
  };

  const getCurrentVariant = (variantIdInput, variantData) => {
    const currentVariantId = Number(variantIdInput?.value);
    return variantData.find((variant) => Number(variant.id) === currentVariantId) || null;
  };

  const getVariantColorValue = (variant, colorOptionPosition) => {
    if (!variant || !colorOptionPosition) return '';

    const optionKey = `option${colorOptionPosition}`;
    return variant[optionKey] || variant.options?.[colorOptionPosition - 1] || '';
  };

  const syncMainVariantColor = (variantSelects, colorOptionName, nextColorValue) => {
    if (!variantSelects || !colorOptionName || !nextColorValue) return;

    const colorRadios = Array.from(
      variantSelects.querySelectorAll('input[type="radio"]')
    ).filter((input) => input.name === colorOptionName);

    if (colorRadios.length) {
      const targetRadio = colorRadios.find((input) => input.value === nextColorValue && !input.disabled);
      if (targetRadio && !targetRadio.checked) {
        targetRadio.checked = true;
        targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const colorSelect = Array.from(variantSelects.querySelectorAll('select')).find(
      (select) => select.name === `options[${colorOptionName}]`
    );

    if (!colorSelect || colorSelect.value === nextColorValue) return;

    const targetOption = Array.from(colorSelect.options).find(
      (option) => option.value === nextColorValue && !option.disabled
    );

    if (!targetOption) return;

    colorSelect.value = nextColorValue;
    colorSelect.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const bindBundleSelector = (container) => {
    const productInfo = container.closest('product-info');
    const quantityInput = productInfo?.querySelector('.quantity__input');
    const variantSelects = productInfo?.querySelector('variant-selects');
    const variantData = parseVariantData(variantSelects);
    const productForm = document.getElementById(container.dataset.productFormId);
    const variantIdInput = productForm?.querySelector('input[name="id"]');
    const cards = Array.from(container.querySelectorAll('[data-bundle-card]'));
    const moneyFormat = container.dataset.moneyFormat || '';
    const colorOptionName = container.dataset.colorOptionName || '';
    const colorOptionPosition = Number(container.dataset.colorOptionPosition || 0);
    const defaultColorValue = container.dataset.defaultColorValue || '';

    if (!quantityInput || !cards.length) return;

    const getCurrentColorValue = () => {
      return (
        getVariantColorValue(getCurrentVariant(variantIdInput, variantData), colorOptionPosition) ||
        defaultColorValue
      );
    };

    const getSelectedOptionValues = () => {
      if (!variantSelects) return [];

      return Array.from(variantSelects.querySelectorAll('select, fieldset'), (element) => {
        if (element.tagName === 'SELECT') {
          return element.value;
        }

        return Array.from(element.querySelectorAll('input[type="radio"]')).find((input) => input.checked)?.value || '';
      });
    };

    const isColorAvailableForCurrentSelection = (colorValue) => {
      if (!variantData.length || !colorOptionPosition) return true;

      const selectedOptions = getSelectedOptionValues();

      return variantData.some((variant) => {
        if (!variant.available) return false;
        if (getVariantColorValue(variant, colorOptionPosition) !== colorValue) return false;

        return selectedOptions.every((selectedValue, index) => {
          const optionPosition = index + 1;
          if (optionPosition === colorOptionPosition || !selectedValue) return true;

          return variant[`option${optionPosition}`] === selectedValue;
        });
      });
    };

    const updateColorSelectAvailability = () => {
      const currentColorValue = getCurrentColorValue();

      cards.forEach((card) => {
        const colorSelects = Array.from(card.querySelectorAll('[data-bundle-color-select]'));

        colorSelects.forEach((select) => {
          const options = Array.from(select.options);

          options.forEach((option) => {
            option.disabled = !isColorAvailableForCurrentSelection(option.value);
          });

          const currentOption = options.find((option) => option.value === select.value && !option.disabled);
          const preferredOption = options.find((option) => option.value === currentColorValue && !option.disabled);
          const firstAvailableOption = options.find((option) => !option.disabled);

          if (!currentOption) {
            select.value = preferredOption?.value || firstAvailableOption?.value || '';
          }
        });
      });
    };

    const updateCardPricing = () => {
      const currentVariant = getCurrentVariant(variantIdInput, variantData);
      if (!currentVariant) return;

      cards.forEach((card) => {
        const quantity = Number(card.dataset.bundleQuantity || 1);
        const price = Number(currentVariant.price || 0) * quantity;
        const compareAtSource = Number(currentVariant.compare_at_price || currentVariant.price || 0);
        const compareAtPrice = compareAtSource * quantity;
        const priceNode = card.querySelector('[data-bundle-price]');
        const compareNode = card.querySelector('[data-bundle-compare]');
        const discountBadge = card.querySelector('[data-bundle-discount-badge]');

        if (priceNode) {
          priceNode.textContent = formatBundleMoney(price, moneyFormat);
        }

        if (compareNode) {
          compareNode.textContent = formatBundleMoney(compareAtPrice, moneyFormat);
          compareNode.classList.toggle('hidden', compareAtPrice <= price);
        }

        if (discountBadge?.dataset.bundleAutoDiscount === 'true') {
          if (compareAtPrice > price) {
            const discountPercent = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
            discountBadge.hidden = false;
            discountBadge.textContent = `${DISCOUNT_LABEL} ${discountPercent}%`;
          } else {
            discountBadge.hidden = true;
          }
        }
      });
    };

    const updateUnitOneSelects = () => {
      const currentColorValue = getCurrentColorValue();
      if (!currentColorValue) return;

      cards.forEach((card) => {
        const firstSelect = card.querySelector('[data-bundle-color-select][data-bundle-unit="1"]');
        if (!firstSelect) return;

        const matchingOption = Array.from(firstSelect.options).find(
          (option) => option.value === currentColorValue
        );

        if (matchingOption) {
          firstSelect.value = currentColorValue;
        }
      });
    };

    const activateCard = (quantity) => {
      cards.forEach((card) => {
        const isActive = Number(card.dataset.bundleQuantity) === Number(quantity);
        const toggle = card.querySelector('[data-bundle-toggle]');
        const details = card.querySelector('[data-bundle-card-details]');
        const colorSelects = Array.from(card.querySelectorAll('[data-bundle-color-select]'));

        card.classList.toggle('is-active', isActive);

        if (toggle) {
          toggle.setAttribute('aria-pressed', String(isActive));
          toggle.setAttribute('aria-expanded', String(isActive));
        }

        if (details) {
          details.hidden = !isActive;
        }

        colorSelects.forEach((select) => {
          select.disabled = !isActive;
        });
      });

      updateColorSelectAvailability();
      updateUnitOneSelects();
    };

    const setQuantity = (quantity) => {
      quantityInput.value = quantity;
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      activateCard(quantity);
    };

    const syncBundleFromQuantity = () => {
      const quantity = Number(quantityInput.value || 1);
      const matchingCard = cards.find(
        (card) => Number(card.dataset.bundleQuantity) === quantity
      );

      if (matchingCard) {
        activateCard(quantity);
        return;
      }

      const recommendedCard = container.querySelector('[data-bundle-recommended="true"]');
      if (recommendedCard) {
        setQuantity(Number(recommendedCard.dataset.bundleQuantity));
        return;
      }

      activateCard(Number(cards[0]?.dataset.bundleQuantity || 1));
    };

    cards.forEach((card) => {
      const toggle = card.querySelector('[data-bundle-toggle]');
      const colorSelects = Array.from(card.querySelectorAll('[data-bundle-color-select]'));

      toggle?.addEventListener('click', () => {
        setQuantity(Number(card.dataset.bundleQuantity));
      });

      colorSelects.forEach((select) => {
        select.addEventListener('change', () => {
          if (select.dataset.bundleUnit === '1') {
            syncMainVariantColor(variantSelects, colorOptionName, select.value);
          }
        });
      });
    });

    quantityInput.addEventListener('change', syncBundleFromQuantity);
    variantIdInput?.addEventListener('change', () => {
      updateCardPricing();
    });

    syncBundleFromQuantity();
    updateCardPricing();
  };

  const init = () => {
    document.querySelectorAll('[data-product-bundles]').forEach((container) => {
      if (container.dataset.bundleReady === 'true') return;
      container.dataset.bundleReady = 'true';
      bindBundleSelector(container);
    });
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
